import { useState, useEffect } from "react";
import { Loader2, Wallet, ExternalLink, Eye, ShieldCheck, Zap, ArrowRightLeft, Activity } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { useApp } from "@/context/AppContext";
import { BondedRow } from "@/components/NurseryPanel";
import { getSnapshot } from "@/lib/nurseryEngine";

interface WalletIntelligence {
  address: string;
  isWhale: boolean;
  whaleData: any;
  isDeployer: boolean;
  deployerData: any;
  isTracked: boolean;
  isCopyTradeTarget: boolean;
  heliusData?: any;
}

export function WalletIntelligenceView({ address }: { address: string }) {
  const { publicKey } = useWallet();
  const { addAppToast } = useApp() as any;
  const [intel, setIntel] = useState<WalletIntelligence | null>(null);
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [splTokens, setSplTokens] = useState<Array<{ mint: string; balance: number; decimals: number, meta?: any }>>([]);
  const [spamAssets, setSpamAssets] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [launchedCoins, setLaunchedCoins] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    let isMounted = true;
    
    async function fetchAll() {
      setIsLoading(true);
      
      try {
        // 1. Fetch Backend Intel & Helius Data natively from Corpus
        const intelRes = await fetch(`/api/wallet-intelligence/${address}`);
        if (intelRes.ok && isMounted) {
          const data = await intelRes.json();
          setIntel(data);
          
          if (data.heliusData) {
            setSolBalance(data.heliusData.solBalance ?? null);
            setSplTokens(
              (data.heliusData.topAssets || []).map((asset: any) => ({
                mint: asset.mint,
                balance: asset.amount,
                decimals: 0, 
                meta: { name: asset.name, symbol: asset.symbol, imageUri: asset.logo }
              }))
            );
            setSpamAssets(data.heliusData.spamAssets || []);
            setRecentActivity(data.heliusData.recentActivity || []);
          }
        }

        // 2. Fetch launched coins from Nursery
        const snap = getSnapshot();
        const allTokens = [...(snap.visibleTokens || []), ...(snap.recentDeployments || [])];
        const launched = allTokens.filter(t => t.creator === address);
        
        // Deduplicate
        const uniqueLaunched = Array.from(new Map(launched.map(t => [t.mint, t])).values());
        setLaunchedCoins(uniqueLaunched);

        // 6. Automatically track this wallet as a Dev
        try {
          await fetch("/api/deployer-rules", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              address: address,
              rule: { type: "track", name: "Auto-tracked from Spotlight", color: "emerald" }
            })
          });
        } catch (e) {
          console.error("Failed to auto-track dev", e);
        }
      } catch (err) {
        console.error("Error fetching wallet intel", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }
    
    fetchAll();
    
    return () => { isMounted = false; };
  }, [address]);

  const handleTrackWallet = async () => {
    try {
      await fetch("/api/wallets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publicKey: address, name: "Tracked from Spotlight" })
      });
      setIntel(prev => prev ? { ...prev, isTracked: true } : null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCopyTrade = async () => {
    if (!publicKey) {
      addAppToast("Please connect a wallet first to start copy trading.", "error");
      return;
    }
    try {
      const res = await fetch("/api/copy-trade", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          targetWallet: address, 
          followerWallet: publicKey,
          buyAmountSol: "0.1",
          maxDailySol: "5"
        })
      });
      if (res.ok) {
        setIntel(prev => prev ? { ...prev, isCopyTradeTarget: true } : null);
        addAppToast("Copy trade successfully activated for 0.1 SOL!", "success");
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (isLoading && !intel && solBalance === null) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <Loader2 className="size-8 text-emerald-400 animate-spin" />
        <div className="text-white/40 font-mono text-xs uppercase tracking-widest">Scanning Wallet Intelligence...</div>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-6">
      <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
        <div className="size-20 sm:size-24 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center shadow-2xl shrink-0">
          <Wallet className="size-10 text-indigo-400" />
        </div>
        
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex flex-wrap justify-center sm:justify-start gap-2">
            <h2 className="text-xl font-bold font-mono text-white truncate max-w-full">
              {address}
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center sm:justify-start gap-2">
            {intel?.isWhale && (
              <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/40 text-blue-400 text-[10px] font-black rounded flex items-center gap-1 uppercase">
                Whale
              </span>
            )}
            {intel?.isDeployer && (
              <span className="px-2 py-0.5 bg-amber-500/20 border border-amber-500/40 text-amber-400 text-[10px] font-black rounded flex items-center gap-1 uppercase">
                <ShieldCheck className="size-3" /> Dev
              </span>
            )}
            {intel?.isTracked && (
              <span className="px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-[10px] font-black rounded flex items-center gap-1 uppercase">
                <Eye className="size-3" /> Tracked
              </span>
            )}
            {intel?.isCopyTradeTarget && (
              <span className="px-2 py-0.5 bg-rose-500/20 border border-rose-500/40 text-rose-400 text-[10px] font-black rounded flex items-center gap-1 uppercase">
                <Zap className="size-3" /> Copy Target
              </span>
            )}
          </div>

          <div className="flex justify-center sm:justify-start items-center gap-3 pt-2">
            {!intel?.isTracked && (
              <button onClick={handleTrackWallet} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold transition-colors flex items-center gap-2">
                <Eye className="size-3.5" /> Track
              </button>
            )}
            {!intel?.isCopyTradeTarget && (
              <button onClick={handleCopyTrade} className="px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 text-xs font-bold transition-colors flex items-center gap-2">
                <Zap className="size-3.5" /> Copy Trade
              </button>
            )}
            <a href={`https://solscan.io/account/${address}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold transition-colors flex items-center gap-2 text-white/70">
              <ExternalLink className="size-3.5" /> Solscan
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 border-b border-white/10 pb-6 mb-2">
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Native SOL Balance</div>
          <div className="text-xl font-bold text-emerald-400">
            {solBalance !== null ? `${solBalance.toFixed(4)} SOL` : "—"}
          </div>
        </div>
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Top SPL Holdings</div>
          <div className="text-xl font-bold text-white">
            {splTokens.length}
          </div>
        </div>
      </div>

      {/* Corpus Intelligence Section */}
      {(intel?.isWhale || intel?.isDeployer) && (
        <div className="space-y-3 pb-4 border-b border-white/10">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <span className="text-indigo-400">🧠</span> Corpus Intelligence
          </h3>
          
          {intel.isWhale && intel.whaleData && (
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 space-y-3">
              <div className="flex items-center gap-2 text-blue-400 font-bold uppercase tracking-wider text-xs">
                Whale Profile {intel.whaleData.displayName ? `- ${intel.whaleData.displayName}` : ''}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[9px] text-blue-400/60 uppercase">Vol (30d)</div>
                  <div className="text-sm font-mono text-white">${intel.whaleData.totalVolume?.toLocaleString() || 0}</div>
                </div>
                <div>
                  <div className="text-[9px] text-blue-400/60 uppercase">Activity</div>
                  <div className="text-sm font-mono text-white">{intel.whaleData.activityCount || 0} tx</div>
                </div>
                <div>
                  <div className="text-[9px] text-blue-400/60 uppercase">Signal Score</div>
                  <div className="text-sm font-mono text-emerald-400">{intel.whaleData.signalScore || 0}/100</div>
                </div>
              </div>
            </div>
          )}

          {intel.isDeployer && intel.deployerData && (
            <div className={`p-4 rounded-xl bg-${intel.deployerData.rule?.color || 'amber'}-500/10 border border-${intel.deployerData.rule?.color || 'amber'}-500/20 space-y-1`}>
              <div className={`flex items-center gap-2 text-${intel.deployerData.rule?.color || 'amber'}-400 font-bold uppercase tracking-wider text-xs`}>
                <ShieldCheck className="size-4" /> Known Developer
              </div>
              <div className="text-xs text-white/70">
                Rule triggered: <span className="font-bold text-white">{intel.deployerData.rule?.name || 'Internal tracking rule'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-6">
        {/* Launched Coins Section (If Dev) */}
        {launchedCoins.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <span className="text-amber-400">🔥</span> Launched Coins
            </h3>
            <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar border border-amber-500/20 rounded-xl overflow-hidden bg-amber-500/5">
              {launchedCoins.map(token => (
                <BondedRow 
                  key={token.mint} 
                  token={token} 
                  showScore={true} 
                  isSelected={false} 
                  onOpen={() => window.open(`https://solscan.io/token/${token.mint}`, "_blank")} 
                />
              ))}
            </div>
          </div>
        )}

        {/* Open Positions Section */}
        <div className="space-y-3 pt-4 border-t border-white/5">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Open Positions</h3>
          {splTokens.length === 0 && !isLoading && (
            <div className="text-xs text-white/40 font-mono text-center py-4 bg-white/5 rounded-xl">No open positions found.</div>
          )}
          <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
            {splTokens.map(token => {
              // Map to Nursery-compatible token object
              const nurseryCompatibleToken = {
                mint: token.mint,
                symbol: token.meta?.symbol || "SPL",
                name: token.meta?.name || "Unknown Token",
                imageUri: token.meta?.imageUri || "",
                marketCapSol: 0,
                buys1h: 0,
                sells1h: 0,
                volume1hSol: 0,
                traderCount: 0,
                maxSingleBuySol: 0,
                firstSeenMs: Date.now(),
                isRugged: false
              };
              
              return (
                <BondedRow 
                  key={token.mint} 
                  token={nurseryCompatibleToken} 
                  showScore={false} 
                  isSelected={false} 
                  onOpen={() => window.open(`https://solscan.io/token/${token.mint}`, "_blank")} 
                />
              );
            })}
          </div>
        </div>

        {/* Scam / Airdrop NFTs Section */}
        {spamAssets.length > 0 && (
          <div className="space-y-4 pt-6 border-t border-rose-500/10">
            <h3 className="text-sm font-bold text-rose-500 uppercase tracking-wider flex items-center gap-2">
              <ShieldCheck className="size-4" /> Potential Scam Airdrops
            </h3>
            <p className="text-xs text-rose-400/70 pb-2">
              These are compressed NFTs often airdropped by scammers containing phishing links. Do not interact with them.
            </p>
            <div className="space-y-3">
              {spamAssets.map((spam, idx) => (
                <div key={spam.mint + idx} className="flex gap-3 p-3 rounded-lg bg-rose-500/5 border border-rose-500/10 hover:bg-rose-500/10 transition-colors opacity-70">
                  <div className="size-10 rounded overflow-hidden bg-rose-500/20 shrink-0 flex items-center justify-center">
                    {spam.logo ? <img src={spam.logo} className="w-full h-full object-cover" /> : <div className="text-xs font-bold text-rose-500">SCAM</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-bold text-rose-400 truncate">{spam.name} <span className="text-[10px] text-rose-500/50 uppercase ml-1">{spam.symbol}</span></div>
                    </div>
                    <div className="text-[10px] text-rose-400/60 mt-1 line-clamp-2">
                      {spam.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Trades Section */}
        <div className="space-y-3 pt-4 border-t border-white/5">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Recent Trades & Transfers</h3>
          {recentActivity.length === 0 && !isLoading && (
            <div className="text-xs text-white/40 font-mono text-center py-4 bg-white/5 rounded-xl">No recent activity found on Helius.</div>
          )}
          
          <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
            {recentActivity.map((tx, idx) => {
              const isSwap = tx.type === "SWAP";
              const Icon = isSwap ? ArrowRightLeft : Activity;
              const date = new Date(tx.timestamp * 1000);
              const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
              
              return (
                <div key={tx.signature + idx} className="flex flex-col gap-2 p-3 rounded-lg bg-black/40 border border-white/5 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`size-10 rounded-full flex items-center justify-center shrink-0 ${isSwap ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-white/5 text-white/40 border border-white/10'}`}>
                      <Icon className="size-5" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-bold text-white uppercase">{tx.type}</div>
                        <div className="text-[10px] text-white/40 font-mono">{dateStr} {timeStr}</div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] text-white/40 font-mono truncate max-w-[150px]">
                          <a href={`https://solscan.io/tx/${tx.signature}`} target="_blank" rel="noreferrer" className="hover:text-emerald-400 transition-colors underline decoration-white/20 underline-offset-2">
                            {tx.signature.substring(0, 8)}...{tx.signature.substring(tx.signature.length - 8)}
                          </a>
                        </div>
                        {tx.amount !== undefined && (
                          <div className={`text-sm font-bold font-mono ${tx.type === 'SWAP' ? 'text-indigo-400' : 'text-white/70'}`}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(4)} SOL
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {tx.description && (
                    <div className="text-[11.5px] text-white/70 bg-white/[0.03] rounded px-3 py-2 leading-snug font-medium border border-white/5">
                      {tx.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
